export type ReleaseKind = "Foundational" | "New feature" | "Enhancement" | "Bug fix";

export const RELEASE_KINDS: ReleaseKind[] = [
  "Foundational",
  "New feature",
  "Enhancement",
  "Bug fix",
];

export type ReleaseNoteEntry = {
  id: string;
  heading: string;
  date: string | null;
  kind: ReleaseKind | null;
  title: string;
  bullets: string[];
  searchText: string;
};

const MAX_ENTRIES = 20;

const KIND_RE =
  /\b(Foundational|New feature|Enhancement|Bug fix)\b/i;

function parseKind(heading: string): ReleaseKind | null {
  const m = heading.match(KIND_RE);
  if (!m) return null;
  const raw = m[1];
  const normalized = RELEASE_KINDS.find((k) => k.toLowerCase() === raw.toLowerCase());
  return normalized ?? null;
}

/** Parse CHANGELOG.md `##` sections into structured entries (newest first). */
export function parseReleaseNotes(markdown: string, max = MAX_ENTRIES): ReleaseNoteEntry[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const headingIdx: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (/^##\s+/.test(lines[i])) headingIdx.push(i);
  }
  const entries: ReleaseNoteEntry[] = [];
  for (let h = 0; h < headingIdx.length && entries.length < max; h++) {
    const start = headingIdx[h];
    const end = h + 1 < headingIdx.length ? headingIdx[h + 1] : lines.length;
    const heading = lines[start].replace(/^##\s+/, "").trim();
    const dateMatch = heading.match(/—\s*(\d{4}-\d{2}-\d{2})/) || heading.match(/(\d{4}-\d{2}-\d{2})/);
    const date = dateMatch?.[1] ?? null;
    const kind = parseKind(heading);
    let title = "";
    const bullets: string[] = [];
    for (let i = start + 1; i < end; i++) {
      const line = lines[i].trim();
      if (/^###\s+/.test(line)) {
        if (!title) title = line.replace(/^###\s+/, "").trim();
        continue;
      }
      if (line.startsWith("- ") || line.startsWith("* ")) {
        bullets.push(line.replace(/^[-*]\s+/, "").replace(/\*\*/g, ""));
      }
    }
    if (!title) title = heading.split("—")[0].trim();
    const searchText = [heading, kind ?? "", title, ...bullets].join(" ").toLowerCase();
    entries.push({
      id: `rn-${entries.length}-${heading.slice(0, 24)}`,
      heading,
      date,
      kind,
      title,
      bullets,
      searchText,
    });
  }
  return entries;
}

export function filterReleaseNotes(entries: ReleaseNoteEntry[], query: string): ReleaseNoteEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries;
  return entries.filter((e) => e.searchText.includes(q));
}

export function releaseKindTone(kind: ReleaseKind | null): string {
  switch (kind) {
    case "Foundational":
      return "bg-navy/10 text-navy";
    case "New feature":
      return "bg-teal/15 text-teal";
    case "Enhancement":
      return "bg-gold/20 text-navy";
    case "Bug fix":
      return "bg-red-500/10 text-red-700";
    default:
      return "bg-mist text-slate";
  }
}

export { MAX_ENTRIES as RELEASE_NOTES_MAX };
